// pages/UserAdmin/crm/BulkTemplateDialog.js
//
// Disparo de template para vários contatos, com conferência ANTES de sair
// qualquer mensagem: "34 prontos · 6 sem o dado".
//
// ─── Por que a conferência existe ──────────────────────────────────────
// Template com {{n}} só sai se todo parâmetro tiver valor. Sem esta tela, a
// pessoa descobria contato a contato durante o disparo — com parte das
// mensagens já enviadas e cobradas no cartão dela pela Meta. Aqui a conta
// aparece antes, e quem está bloqueado aparece com o motivo por nome.
//
// ─── Por que o laço roda no NAVEGADOR ──────────────────────────────────
// O Procfile sobe `uvicorn --workers 1` e não há Celery/Redis. Um laço de
// 40 envios no servidor levaria ~20s segurando o único worker: chat,
// webhook da Meta e todo o resto do sistema parariam pra todos os clientes
// durante o disparo. Então o laço é aqui, um POST /messages/send por
// contato, em sequência. O custo é que fechar a aba interrompe — por isso
// a barra de progresso diz quantos já saíram, e o resumo final lista
// nominalmente quem não recebeu.
//
// Cada envio passa pelas travas que já existem no backend (teto diário por
// chip e débito do ledger). Se uma delas estourar no meio, o laço PARA em
// vez de insistir: o resto falharia igual e só geraria ruído.
//
// ─── Valor digitado vale pra todos ─────────────────────────────────────
// Variável com origem "Perguntar no envio" recebe um valor único para o
// lote inteiro (promoção, data de evento, aviso). Dado que muda por pessoa
// — valor de boleto, código de rastreio — pertence a um campo
// personalizado do CRM, e a tela diz isso em vez de pedir 40 valores na
// mão: 40 campos digitados à mão é onde erro humano entra.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, LinearProgress, MenuItem, Stack,
  TextField, Typography,
} from '@mui/material';
import api from '../../../services/Api';
import { Icon } from './icons';
import { mensagemDeErro } from './erros';
import { novoLoteId } from '../../../utils/lote';
import ProtecaoDisparo, { bloqueioDoDisparo } from '../../../components/ProtecaoDisparo';

const SYNE = "'Syne', system-ui, sans-serif";
// Verde da marca pelo tema (claro/escuro).
const GREEN = 'var(--ch-green)';

// Pausa entre um envio e o outro. Os POSTs já são sequenciais (cada um
// espera a Graph API), então isto é só uma folga extra: rajada de template
// é exatamente o padrão que derruba a qualidade do chip na Meta.
const PAUSA_ENTRE_ENVIOS_MS = 350;

const dorme = (ms) => new Promise((r) => setTimeout(r, ms));

/** Erro que não adianta tentar no próximo contato: teto diário do chip,
 *  ledger sem saldo, assinatura inativa. Faz o laço parar em vez de
 *  colecionar 40 falhas idênticas.
 *
 *  Decidido por STATUS, não por texto da mensagem. A primeira versão disto
 *  procurava palavras ("teto", "saldo") e não pegou o caso real — as duas
 *  travas de volume do backend respondem 429 com a frase "Limite mensal de
 *  proteção atingido", que não contém nenhuma delas, e o laço seguiu
 *  batendo 40 vezes na mesma parede. Status é contrato; texto é copy e
 *  muda. */
function ehErroQueParaTudo(err) {
  const status = err?.response?.status;
  // 429: teto diário por chip e ledger mensal (check_daily_template_cap e
  // check_and_debit_template_ledger). 402/403: cobrança e assinatura.
  // 423: teto de custo da Meta do mês atingido com o bloqueio de campanhas
  // ligado (Administração Meta, out/26) — vale pra todos os que faltam.
  // 422/428 (blindagem, out/26): tema proibido no template e falta da
  // confirmação de opt-in valem pro lote inteiro, não pra um contato.
  return status === 429 || status === 402 || status === 403 || status === 423
    || status === 422 || status === 428;
}

function msgDoErro(err) {
  return mensagemDeErro(err, 'Falha no envio.');
}

/** Linha de um contato na lista de conferência. */
function LinhaContato({ contato }) {
  const ok = contato.ready;
  return (
    <Stack
      direction="row" alignItems="flex-start" spacing={1}
      sx={{ py: 0.6, borderBottom: '1px solid var(--ch-bdr)' }}
    >
      <Box sx={{ color: ok ? 'var(--ch-green-d)' : 'var(--ch-amber-d)', mt: '2px', flexShrink: 0 }}>
        {ok ? <Icon.Confirmed size={13} /> : <Icon.Alert size={13} />}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" sx={{ color: 'var(--ch-text)', fontWeight: 600, display: 'block' }}>
          {contato.client_name}
        </Typography>
        {!ok && (
          <Typography variant="caption" sx={{ color: 'var(--ch-amber-d)', display: 'block', lineHeight: 1.35 }}>
            {contato.missing.map((m) => m.reason).join(' · ')}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

export default function BulkTemplateDialog({
  open, onClose, clientIds = [], onDone,
}) {
  const token = localStorage.getItem('token');
  const auth = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }), [token]
  );

  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [carregandoTpl, setCarregandoTpl] = useState(false);

  const [preflight, setPreflight] = useState(null);
  const [conferindo, setConferindo] = useState(false);
  const [erro, setErro] = useState('');

  // Valores digitados para as variáveis "Perguntar no envio", por slot.
  const [respostas, setRespostas] = useState({});
  // Caixa de opt-in (blindagem, out/26). Volta a desmarcada a cada
  // conferência: confirmar vale para ESTE disparo, não para o próximo.
  const [optin, setOptin] = useState(false);

  // Progresso do disparo. `enviando` separado de `progresso` porque a tela
  // continua mostrando o resumo depois que o laço termina.
  const [enviando, setEnviando] = useState(false);
  const [progresso, setProgresso] = useState(null);  // {feitos,total,falhas[],parado}
  const cancelar = useRef(false);

  const template = useMemo(
    () => templates.find((t) => t.name === templateName) || null,
    [templates, templateName]
  );

  // ─── Carga inicial: chips da empresa ───────────────────────────────────
  useEffect(() => {
    if (!open) return;
    let vivo = true;
    api.get('/templates/meta-info', auth)
      .then((res) => {
        if (!vivo) return;
        const lista = res.data?.devices || [];
        setDevices(lista);
        if (lista.length === 1) setDeviceId(lista[0].id);
      })
      .catch(() => { if (vivo) setErro('Não foi possível carregar os chips.'); });
    return () => { vivo = false; };
  }, [open, auth]);

  // ─── Templates do chip escolhido — só os aprovados ─────────────────────
  // Template PENDING ou REJECTED não sai; oferecer na lista só produziria
  // 40 falhas idênticas depois.
  useEffect(() => {
    if (!open || !deviceId) { setTemplates([]); return undefined; }
    let vivo = true;
    setCarregandoTpl(true);
    setTemplateName('');
    setPreflight(null);
    api.get(`/templates/list/${deviceId}`, auth)
      .then((res) => {
        if (!vivo) return;
        setTemplates((res.data?.templates || []).filter(
          (t) => String(t.status).toUpperCase() === 'APPROVED'
        ));
      })
      .catch((err) => { if (vivo) setErro(msgDoErro(err)); })
      .finally(() => { if (vivo) setCarregandoTpl(false); });
    return () => { vivo = false; };
  }, [open, deviceId, auth]);

  // ─── Conferência ───────────────────────────────────────────────────────
  const conferir = useCallback(async (overrides) => {
    if (!deviceId || !templateName) return;
    setOptin(false);
    setConferindo(true);
    setErro('');
    try {
      const res = await api.post(`/templates/preflight/${deviceId}`, {
        template_name: templateName,
        language: template?.language || 'pt_BR',
        client_ids: clientIds,
        overrides: overrides || {},
      }, auth);
      setPreflight(res.data);
    } catch (err) {
      setErro(msgDoErro(err));
      setPreflight(null);
    } finally {
      setConferindo(false);
    }
  }, [deviceId, templateName, template?.language, clientIds, auth]);

  // Primeira conferência ao escolher o template. As seguintes são
  // disparadas pelo botão "Conferir de novo" depois de digitar os valores
  // — refazer a cada tecla renderia uma chamada por caractere.
  useEffect(() => {
    if (open && templateName) {
      setRespostas({});
      conferir({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templateName]);

  // Slots que a pessoa precisa preencher: os de origem "Perguntar no envio".
  const slotsPerguntados = useMemo(() => (
    (preflight?.mapping || [])
      .filter((e) => e.source_kind === 'ASK')
      .map((e) => ({ slot: `${e.component}:${e.position}`, position: e.position, component: e.component }))
  ), [preflight?.mapping]);

  // Bloqueio que NÃO é "perguntar no envio" — contato sem o campo do CRM
  // preenchido. Digitar não resolve; é preciso preencher no contato.
  const bloqueadosPorDado = useMemo(() => (
    (preflight?.contacts || []).filter((c) => !c.ready)
  ), [preflight?.contacts]);

  const prontos = useMemo(() => (
    (preflight?.contacts || []).filter((c) => c.ready)
  ), [preflight?.contacts]);

  const exemploPreview = prontos[0]?.preview || preflight?.contacts?.[0]?.preview || '';

  // Template com {{n}} e SEM mapeamento salvo não pode sair daqui, mesmo
  // que a conferência diga "pronto". Sem mapeamento, toda variável vira
  // "digitar no envio" (é a regra do preflight, e desde out/26 também a do
  // /messages/send, que passou a usar o que foi digitado). No chat isso
  // serve: é um contato. No lote, o valor digitado iria IGUAL para os 40 —
  // o mesmo nome para todo mundo. Bloquear com o caminho da solução
  // (mapear o template) é melhor que 40 mensagens erradas.
  const semMapeamento = Boolean(
    preflight && preflight.has_variables && !preflight.mapped
  );

  // ─── Disparo ───────────────────────────────────────────────────────────
  const disparar = async () => {
    if (!prontos.length) return;
    cancelar.current = false;
    setEnviando(true);
    setErro('');
    const falhas = [];
    let feitos = 0;
    let parado = null;

    setProgresso({ feitos: 0, total: prontos.length, falhas: [], parado: null });

    const loteId = novoLoteId();
    for (const contato of prontos) {
      if (cancelar.current) { parado = 'cancelado'; break; }
      try {
        await api.post('/messages/send', {
          device_id: deviceId,
          to: contato.phone_number,
          template_name: templateName,
          language: preflight?.language || template?.language || 'pt_BR',
          template_vars: respostas,
          // Blindagem (out/26): a caixa de opt-in marcada e o que a tela
          // mostrou — o servidor grava uma confirmação por lote.
          optin_confirmado: optin,
          lote_total: prontos.length,
          lote_frios: preflight?.frios ?? null,
          // Estrutura do template pra bolha do chat ficar igual à que o
          // cliente recebeu (out/26). Vai daqui porque esta tela já tem —
          // sem isso o servidor consultaria a Meta a cada contato do lote.
          template_struct: Array.isArray(template?.components)
            ? template.components : undefined,
          // Marca de envio em lote: com o teto de custo da Meta estourado e
          // o bloqueio ligado, o servidor recusa (423). O template avulso do
          // chat não manda a marca e nunca é barrado.
          campanha: true,
          // Mesmo id em toda a rodada (Formulários PR 3): agrupa as
          // respostas do formulário do botão por disparo.
          lote_id: loteId,
        }, auth);
        feitos += 1;
      } catch (err) {
        falhas.push({ nome: contato.client_name, motivo: msgDoErro(err) });
        if (ehErroQueParaTudo(err)) { parado = msgDoErro(err); break; }
      }
      setProgresso({ feitos, total: prontos.length, falhas: [...falhas], parado: null });
      if (!cancelar.current) await dorme(PAUSA_ENTRE_ENVIOS_MS);
    }

    setProgresso({ feitos, total: prontos.length, falhas, parado });
    setEnviando(false);
    onDone?.({ enviados: feitos, falhas: falhas.length });
  };

  const fechar = () => {
    if (enviando) return;
    setPreflight(null);
    setTemplateName('');
    setRespostas({});
    setOptin(false);
    setProgresso(null);
    setErro('');
    onClose?.();
  };

  const terminou = progresso && !enviando;
  // O contato em que o lote parou (teto de custo, ledger, assinatura) repete
  // palavra por palavra o aviso "O disparo parou" — no celular o mesmo
  // parágrafo aparecia duas vezes (out/26). Some só da lista; a contagem do
  // `onDone` continua com ele.
  const falhasVisiveis = progresso
    ? progresso.falhas.filter((f) => !progresso.parado || f.motivo !== progresso.parado)
    : [];
  const faltaResponder = slotsPerguntados.some((s) => !(respostas[s.slot] || '').trim());
  const bloqueio = bloqueioDoDisparo(preflight);

  return (
    <Dialog
      open={open} onClose={fechar} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 4 } }}
    >
      <DialogTitle sx={{ fontFamily: SYNE, fontWeight: 800, color: 'var(--ch-text)', pb: 1 }}>
        Enviar template
        <Typography variant="caption" sx={{ display: 'block', color: 'var(--cx-cap)', fontWeight: 500 }}>
          {clientIds.length} contato{clientIds.length === 1 ? '' : 's'} selecionado
          {clientIds.length === 1 ? '' : 's'}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {erro && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{erro}</Alert>}

        {/* ─── Resultado do disparo ─── */}
        {progresso ? (
          <Stack spacing={2}>
            <LinearProgress
              variant="determinate"
              value={progresso.total ? (progresso.feitos / progresso.total) * 100 : 0}
              sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: GREEN } }}
            />
            <Typography sx={{ fontFamily: SYNE, fontWeight: 800, color: 'var(--ch-text)' }}>
              {progresso.feitos} de {progresso.total} enviado
              {progresso.feitos === 1 ? '' : 's'}
            </Typography>

            {progresso.parado === 'cancelado' && (
              <Alert severity="warning" sx={{ borderRadius: 2, fontSize: '0.82rem' }}>
                Disparo cancelado. Os {progresso.feitos} primeiros já saíram e
                não dá pra desfazer — a Meta já entregou.
              </Alert>
            )}
            {progresso.parado && progresso.parado !== 'cancelado' && (
              <Alert severity="error" sx={{ borderRadius: 2, fontSize: '0.82rem' }}>
                O disparo parou: {progresso.parado} Os contatos restantes não
                foram enviados.
              </Alert>
            )}

            {falhasVisiveis.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ color: 'var(--ch-red-d)', fontWeight: 700 }}>
                  {falhasVisiveis.length} não {falhasVisiveis.length === 1 ? 'saiu' : 'saíram'}:
                </Typography>
                <Box sx={{ maxHeight: 160, overflow: 'auto', mt: 0.5 }}>
                  {falhasVisiveis.map((f, i) => (
                    <Typography key={i} variant="caption" sx={{ display: 'block', color: 'var(--cx-cap)' }}>
                      <strong>{f.nome}</strong> — {f.motivo}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {terminou && !progresso.falhas.length && !progresso.parado && (
              <Alert severity="success" sx={{ borderRadius: 2, fontSize: '0.85rem' }}>
                Tudo enviado.
              </Alert>
            )}
          </Stack>
        ) : (
          <Stack
            spacing={2.5}
            // `pt` porque o rótulo flutuante do primeiro campo sobe acima
            // da borda e encostava no subtítulo do diálogo.
            sx={{ pt: 1 }}
          >
            {/* ─── Chip + template ─── */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  select fullWidth size="small" label="Chip (WABA)"
                  value={deviceId}
                  onChange={(e) => setDeviceId(e.target.value)}
                  helperText="De qual número vai sair"
                >
                  {devices.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.name} — {d.phone_number}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  select fullWidth size="small" label="Template"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  disabled={!deviceId || carregandoTpl}
                  helperText={
                    carregandoTpl ? 'Carregando…'
                      : (deviceId && !templates.length) ? 'Nenhum template aprovado neste chip'
                        : 'Só aprovados aparecem aqui'
                  }
                >
                  {templates.map((t) => (
                    <MenuItem key={t.id} value={t.name}>{t.label || t.name}</MenuItem>
                  ))}
                </TextField>
              </Box>
            </Stack>

            {conferindo && (
              <Stack alignItems="center" sx={{ py: 3 }}>
                <CircularProgress size={22} sx={{ color: GREEN }} />
              </Stack>
            )}

            {preflight && !conferindo && (
              <>
                {semMapeamento && (
                  <Alert severity="warning" sx={{ borderRadius: 2, fontSize: '0.82rem' }}>
                    Este template tem variáveis mas ninguém disse ainda de
                    onde sai o valor de cada uma. Abra <strong>Meta Business
                    → Ver e editar templates</strong> e clique em
                    <strong> Variáveis</strong> neste template. Sem isso o
                    disparo sairia sem os parâmetros e a Meta recusaria
                    todos.
                  </Alert>
                )}

                {preflight.stale && (
                  <Alert severity="warning" sx={{ borderRadius: 2, fontSize: '0.82rem' }}>
                    O template mudou na Meta depois que o mapeamento foi
                    salvo. Revise em Meta Business → Ver e editar templates →
                    Variáveis antes de disparar.
                  </Alert>
                )}

                {/* ─── O placar ─── */}
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={`${preflight.ready_count} pronto${preflight.ready_count === 1 ? '' : 's'}`}
                    sx={{ bgcolor: 'var(--ch-green-bg)', color: 'var(--ch-green-d)', fontWeight: 700 }}
                  />
                  {preflight.blocked_count > 0 && (
                    <Chip
                      size="small"
                      label={`${preflight.blocked_count} sem o dado`}
                      sx={{ bgcolor: 'var(--ch-amber-bg)', color: 'var(--ch-amber-d)', fontWeight: 700 }}
                    />
                  )}
                </Stack>

                {/* ─── Variáveis que a pessoa digita ─── */}
                {slotsPerguntados.length > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'var(--ch-text2)', fontWeight: 700, display: 'block', mb: 0.75 }}>
                      Valores deste disparo
                    </Typography>
                    <Stack spacing={1.5}>
                      {slotsPerguntados.map((s) => (
                        <TextField
                          key={s.slot} fullWidth size="small"
                          label={`{{${s.position}}}`}
                          value={respostas[s.slot] || ''}
                          onChange={(e) =>
                            setRespostas((p) => ({ ...p, [s.slot]: e.target.value }))
                          }
                          inputProps={{ maxLength: 300 }}
                          helperText="Vale para todos os contatos deste disparo"
                        />
                      ))}
                      <Button
                        size="small" onClick={() => conferir(respostas)}
                        startIcon={<Icon.Refresh size={13} />}
                        sx={{ textTransform: 'none', color: 'var(--ch-green-d)', alignSelf: 'flex-start', fontWeight: 700 }}
                      >
                        Conferir de novo
                      </Button>
                    </Stack>
                  </Box>
                )}

                {/* ─── Prévia ─── */}
                {exemploPreview && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'var(--ch-text2)', fontWeight: 700, display: 'block', mb: 0.5 }}>
                      Como vai chegar {prontos[0] ? `(exemplo: ${prontos[0].client_name})` : ''}
                    </Typography>
                    <Box sx={{
                      // A bolha imita a do WhatsApp — clara, com texto
                      // escuro, nos dois temas.
                      bgcolor: '#dcf8c6', borderRadius: 2, p: 1.5,
                      fontSize: '0.85rem', color: '#111b21', whiteSpace: 'pre-wrap',
                    }}>
                      {exemploPreview}
                    </Box>
                  </Box>
                )}

                {/* ─── Quem está de fora ─── */}
                {bloqueadosPorDado.length > 0 && (
                  <Box>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="caption" sx={{ color: 'var(--ch-amber-d)', fontWeight: 700, display: 'block', mb: 0.5 }}>
                      Estes {bloqueadosPorDado.length === 1 ? 'fica' : 'ficam'} de fora
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'var(--cx-cap)', display: 'block', mb: 1 }}>
                      Preencha o campo no contato (em Contatos) e confira de
                      novo, ou dispare só para os prontos.
                    </Typography>
                    <Box sx={{ maxHeight: 180, overflow: 'auto' }}>
                      {bloqueadosPorDado.map((c) => (
                        <LinhaContato key={c.client_id} contato={c} />
                      ))}
                    </Box>
                  </Box>
                )}

                <ProtecaoDisparo
                  preflight={preflight} total={prontos.length}
                  optin={optin} onOptin={setOptin}
                />

                <Alert severity="info" sx={{ borderRadius: 2, fontSize: '0.8rem' }}>
                  Cada template é cobrado pela Meta direto no cartão da sua
                  empresa. O envio acontece nesta aba, um contato por vez —
                  se fechar a página, o que faltava não sai.
                </Alert>
              </>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid var(--ch-bdr)' }}>
        {enviando ? (
          <Button
            onClick={() => { cancelar.current = true; }}
            sx={{ textTransform: 'none', fontFamily: SYNE, fontWeight: 700, color: 'var(--ch-red-d)' }}
          >
            Parar
          </Button>
        ) : (
          <Button
            onClick={fechar}
            sx={{ textTransform: 'none', fontFamily: SYNE, fontWeight: 700, color: 'var(--cx-cap)' }}
          >
            {terminou ? 'Fechar' : 'Cancelar'}
          </Button>
        )}

        {!progresso && (
          <Button
            onClick={disparar}
            disabled={
              conferindo || !preflight || !preflight.ready_count
              || faltaResponder || semMapeamento || !!bloqueio || !optin
            }
            variant="contained" disableElevation
            startIcon={<Icon.Send size={14} />}
            sx={{
              textTransform: 'none', fontFamily: SYNE, fontWeight: 700,
            }}
          >
            {bloqueio === 'tema'
              ? 'Template não permitido'
              : bloqueio === 'conta'
                ? 'Disparos parados pela Meta'
                : bloqueio === 'teste'
                  ? 'Limite do teste atingido'
                  : semMapeamento
              ? 'Mapeie as variáveis antes'
              : faltaResponder
                ? 'Preencha os valores'
                : `Disparar para ${preflight?.ready_count || 0}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
